import { useCallback } from 'react';
import { useMqttStore } from '@stores/mqttStore';
import useMqttPublish from '@hooks/useMqttPublish';

type ControlAction = 'START' | 'PAUSE' | 'RESUME' | 'ABORT' | 'FORCE_RESET';

const useExecutionControl = (runId: string | null) => {
  const stationId = useMqttStore(state => state.stationId);
  const { publishCommand, isConnected } = useMqttPublish();

  const sendCommand = useCallback(
    (
      action: ControlAction,
      overrideRunId?: string,
      options?: { setupOnly?: boolean }
    ) => {
      const effectiveRunId = overrideRunId ?? runId;
      if (!stationId || !effectiveRunId) {
        console.warn('[ExecutionControl] Cannot send command — missing', {
          stationId,
          runId: effectiveRunId
        });
        return;
      }
      const topic = `voltbot/${stationId}/script/run/${effectiveRunId}/control`;
      const payload = JSON.stringify({
        action,
        runId: effectiveRunId,
        ...(options?.setupOnly && { setupOnly: true })
      });
      console.log('[ExecutionControl] Publishing', action, 'to', topic);
      publishCommand(topic, payload);
    },
    [stationId, runId, publishCommand]
  );

  const publishStart = useCallback(() => sendCommand('START'), [sendCommand]);
  const publishPause = useCallback(() => sendCommand('PAUSE'), [sendCommand]);
  const publishResume = useCallback(() => sendCommand('RESUME'), [sendCommand]);
  const publishAbort = useCallback(() => sendCommand('ABORT'), [sendCommand]);

  const publishForceReset = useCallback(() => {
    if (!stationId) return;
    const topic = `voltbot/${stationId}/script/run/_reset/control`;
    const payload = JSON.stringify({ action: 'FORCE_RESET' });
    console.log('[ExecutionControl] Publishing FORCE_RESET to', topic);
    publishCommand(topic, payload);
  }, [stationId, publishCommand]);

  const publishRehome = useCallback(() => {
    if (!stationId) return;
    const topic = `voltbot/${stationId}/script/run/_rehome/control`;
    const payload = JSON.stringify({ action: 'REHOME' });
    console.log('[ExecutionControl] Publishing REHOME to', topic);
    publishCommand(topic, payload);
  }, [stationId, publishCommand]);

  return {
    sendCommand,
    publishStart,
    publishPause,
    publishResume,
    publishAbort,
    publishForceReset,
    publishRehome,
    isConnected
  };
};

export default useExecutionControl;
